import React from "react";
import { Divider, Drawer, useTheme } from "@mui/material";
import List from "@mui/material/List";
import { NavigationStyles } from "@components/styles/NavigationStyles";
import { useGetCloudNavItems } from "@features/unified-side-nav-feature/hooks/useGetCloudNavItems";
import { OrganizationNavHeader } from "@features/unified-side-nav-feature/components/OrganizationNavHeader";
import { StorefrontNavHeader } from "@features/unified-side-nav-feature/components/StorefrontNavHeader";
import { useGetSupportNavItems } from "@features/unified-side-nav-feature/hooks/useGetSupportNavItems";
import useGetStorefrontNavItems from "@features/unified-side-nav-feature/hooks/useGetStorefrontNavItems";
import NavItem from "@components/Navigation/NavItem";
import { NavItemType } from "@components/Navigation/NavigationTypes";
import { NavItemsProvider } from "@components/Navigation/NavItemContext";
import { useGetAssetsNavItems } from "@features/unified-side-nav-feature/hooks/useGetAssetsNavItems";
import { useGetManufacturerInsightsNavItems } from "@features/unified-side-nav-feature/hooks/useGetManufacturerInsightsNavItems";
import { useGetProjectNavItems } from "@features/unified-side-nav-feature/hooks/useGetProjectsNavItems";
import { useGetResearchNavItems } from "@features/unified-side-nav-feature/hooks/useGetResearchNavItems";
import { useGetReportsNavItems } from "@features/unified-side-nav-feature/hooks/useGetReportsNavItems";
import { useGetAccountTeamNavItems } from "@features/unified-side-nav-feature/hooks/useGetAccountTeamNavItems";
import useGetSettingsNavItems from "@features/unified-side-nav-feature/hooks/useGetSettingsNavItems";
import { useUnifiedNav } from "@features/unified-side-nav-feature/context/UnifiedNavContext";
import { HEADER_HEIGHT_CSS_VAR } from "shared-ui/src/constants";

export default function UnifiedSideNav() {
  const theme = useTheme();
  const navStyles = NavigationStyles(theme);
  const baseDrawerWidthPx = 0;

  const mockDrawerWidthPx = 280;
  const mockDrawerLeftOffsetPx = baseDrawerWidthPx;

  // Always reserve the top header area so the drawer sits directly below the fixed
  // courtesy header (hamburger/toolbar + impersonation bar when active) AND the ecom
  // TopNavMenu bar (48px dense toolbar) rendered beneath it. Sourced from the
  // HEADER_HEIGHT_CSS_VAR set by CourtesyNavigationHeader so this can never drift
  // out of sync with the header's actual rendered height (previously a hardcoded magic number).
  const mockNavTopOffsetPx = `calc(var(${HEADER_HEIGHT_CSS_VAR}, 108px) + 48px)`;

  const { isNavOpen, isDesktop, closeNav } = useUnifiedNav();


  const accountTeamData: NavItemType[] = useGetAccountTeamNavItems();

  const supportData = useGetSupportNavItems();
  const cloudData = useGetCloudNavItems();
  const storefrontNavItems = useGetStorefrontNavItems();
  const assetsData = useGetAssetsNavItems();
  const manufacturerInsightsData = useGetManufacturerInsightsNavItems();
  const projectsData = useGetProjectNavItems();
  const researchData = useGetResearchNavItems();
  const reportsData = useGetReportsNavItems();
  const settingsData = useGetSettingsNavItems();

  const nonStorefrontNavItems = [
    ...projectsData,
    ...supportData,
    ...cloudData,
    ...assetsData,
    ...manufacturerInsightsData,
    ...researchData,
    ...reportsData,
    ...settingsData,
  ];
  
  const navItems = storefrontNavItems
    ? [
        ...storefrontNavItems,
        ...nonStorefrontNavItems,
      ]
    : [
      ...nonStorefrontNavItems,
    ];

  return (
    <NavItemsProvider>
      <Drawer
        anchor="left"
        open={isNavOpen}
        variant={isDesktop ? "persistent" : "temporary"}
        onClose={closeNav}
        sx={{
          ...navStyles.drawer,
          width: isNavOpen && isDesktop ? `${mockDrawerWidthPx}px` : 0,
          transition: (theme) =>
            theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: isNavOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          "& .MuiPaper-root": {
            ...(navStyles.drawer?.["& .MuiPaper-root"] as object),
            width: `${mockDrawerWidthPx}px`,
            left: isDesktop ? `${mockDrawerLeftOffsetPx}px` : 0,
            marginTop: "0 !important",
            top: isDesktop ? mockNavTopOffsetPx : 0,
            maxHeight: isDesktop
              ? `calc(100dvh - ${mockNavTopOffsetPx}) !important`
              : "100dvh !important",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <OrganizationNavHeader />
        <List
          component="nav"
          sx={{
            ...navStyles.list,
            pt: 0,
            height: "auto",
            flex: "0 0 auto",
            overflowY: "visible",
          }}
        >
          {accountTeamData &&
            accountTeamData.length > 0 &&
            accountTeamData.map(
              (navItem) =>
                navItem && (
                  (<NavItem key={navItem.id} navItem={navItem} tier={1} />)
                ),
            )}
        </List>
        <Divider
          sx={{
            borderColor: (theme) => theme.palette.grey[400],
          }}
        />
        <StorefrontNavHeader />
        <List component="nav" sx={navStyles.list}>
          {navItems &&
            navItems.length > 0 &&
            navItems.map(
              (navItem) =>
                navItem && (
                  // @ts-ignore
                  (<NavItem key={navItem.id} navItem={navItem} tier={1} />)
                ),
            )}
        </List>
      </Drawer>
    </NavItemsProvider>
  );
}